import { Check, Clock, Heart, Plus, Sparkle } from 'lucide-react'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { Chip } from '../../components/chip'
import { Loader } from '../../components/loader'
import { MealPhoto } from '../../components/meal-photo'
import { type PlateRole, PlateRoleChip } from '../../components/plate-role-chip'
import { usePressMotion } from '../../lib/press-motion'
import { cn } from '../../lib/utils'

/**
 * Presentation state of the card photo. String values match web's
 * `MealImagePresentation`, so its members are assignable without a cross-package
 * import (same convention as PlateRole ↔ ComponentRole).
 */
export enum MealCardImageState {
  /** Rendition landed — show the photo. */
  Ready = 'ready',
  /** The image pipeline is still producing the photo — placeholder tile + loader. */
  Loading = 'loading',
  /** Terminal, no photo coming — placeholder tile alone. */
  Placeholder = 'placeholder'
}
type ImageStateValue = `${MealCardImageState}`

/** The one image tag every card carries: amber New (never cooked) or glass Cooked ×N. */
export enum MealCardTagVariant {
  New = 'new',
  Cooked = 'cooked'
}

export interface MealCardTag {
  variant: MealCardTagVariant
  /** Translated label — "New" / "Cooked ×8". */
  label: string
}

export interface MealCardProps {
  name: string
  /** The card photo, or the consumer's stand-in art while it isn't ready. Falls back to the stripe tile when absent. */
  imageUrl?: string
  /** Shown if `imageUrl` fails to load — the meal's placeholder art. The stripe tile is the last resort. */
  fallbackImageUrl?: string
  imageState: ImageStateValue
  /** Pre-formatted, translated meta labels — e.g. "35 min", "Serves 4". */
  timeLabel: string
  servingsLabel?: string
  tag: MealCardTag
  favorite: boolean
  /** Accessible name for the heart toggle, e.g. "Favorite" / "Remove from favorites". */
  favoriteLabel: string
  onFavoriteToggle?: () => void
  /** Open the meal (detail view). The whole card is the hit area via a stretched title button. */
  onOpen?: () => void
  /** Plate-role dots in display order; `rolesLabel` is their tooltip (e.g. "Protein · Starch · Veggie"). */
  roles?: PlateRole[]
  rolesLabel?: string
  /** ⋯ slot beside the title — the consumer supplies its fully-wired menu trigger (e.g. a DropdownMenu). */
  menu?: ReactNode
  /**
   * Hover quick-action over the image (e.g. "+ Plan") — pointer devices only, and
   * deliberately redundant with a menu action (the ⋯ is the touch/discoverable path).
   */
  quickAction?: { label: string; onClick: () => void }
  /**
   * Select mode (bulk management): the whole card becomes a selection toggle — a
   * check circle replaces the heart/quick-action/menu, tapping toggles instead of
   * opening, and `selected` rings the card. `selectLabel` names the toggle for AT.
   */
  selectable?: boolean
  selected?: boolean
  selectLabel?: string
  onSelectToggle?: () => void
  className?: string
}

/**
 * One meal in the recipes library — photo (with loading/placeholder states),
 * favorite heart, New/Cooked ×N tag, name, time + servings meta, plate-role dots,
 * and a ⋯ menu slot. Purely presentational: every label arrives translated, the
 * menu arrives fully wired, and image resolution happens upstream.
 *
 * The whole card opens the meal: the title is a button whose stretched
 * pseudo-element covers the card, so the semantics stay a single button while the
 * heart and menu sit above it (no nested-interactive markup).
 */
export function MealCard({
  name,
  imageUrl,
  fallbackImageUrl,
  imageState,
  timeLabel,
  servingsLabel,
  tag,
  favorite,
  favoriteLabel,
  onFavoriteToggle,
  onOpen,
  roles,
  rolesLabel,
  menu,
  quickAction,
  selectable = false,
  selected = false,
  selectLabel,
  onSelectToggle,
  className
}: MealCardProps) {
  const press = usePressMotion()
  // In select mode the card is a toggle: per-card actions get out of the way
  // (the handoff hides the heart/quick-actions while selecting).
  const tapAction = selectable ? onSelectToggle : onOpen

  return (
    <motion.article
      // Spring-squish on press only when the card actually does something; the
      // hover lift stays CSS (the established card idiom) so the two don't fight.
      {...(tapAction ? { whileTap: press.whileTap, transition: press.transition } : {})}
      // motion adds tabindex="0" to anything carrying a tap gesture, which made the article
      // a focus stop of its own — ahead of the real controls and painting the browser's
      // default outline, since the article has no ring styles. It has no click handler
      // either (the stretched title button is the actual control), so opt it back out.
      tabIndex={-1}
      className={cn(
        // h-full: fill the wrapper (a grid cell / shelf slot), so cards in a row share
        // its height and their mt-auto meta rows align regardless of name length.
        'group relative flex h-full flex-col overflow-hidden rounded-[22px] border border-border bg-card shadow-card',
        // The tight hover shadow (short reach) so the lift survives the shelf rows'
        // overflow clipping; grids read the same, just calmer.
        'transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-[3px] hover:border-gray-300 hover:shadow-card-hover-tight dark:hover:border-gray-600',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        selected && 'ring-[2.5px] ring-brand-green-fill ring-offset-1 ring-offset-background',
        className
      )}>
      <div className="relative aspect-[4/3] shrink-0">
        <MealPhoto src={imageUrl} fallbackSrc={fallbackImageUrl} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        {imageState === MealCardImageState.Loading && (
          <Loader size={26} className="absolute inset-0 z-[1] text-brand-green-600 dark:text-brand-green-300" />
        )}

        {selectable && (
          // Selection state marker — the whole card is the toggle (the stretched
          // title button below), so this circle is presentational.
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-2.5 top-2.5 z-[2] grid h-[26px] w-[26px] place-items-center rounded-full border-2 shadow-[0_1px_6px_rgba(30,51,25,0.2)] backdrop-blur-sm transition-colors',
              selected ? 'border-brand-green-fill bg-brand-green-fill text-white' : 'border-white/95 bg-brand-green-900/25 text-transparent'
            )}>
            <Check size={13} strokeWidth={3} />
          </span>
        )}

        {!selectable && quickAction && (
          // Hover-capable pointers only ([@media(hover:hover)]): touch users reach the
          // same action through the ⋯ menu, so no dead always-visible pill on mobile.
          <div className="absolute left-2 top-2 z-[2] hidden opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:block">
            <button
              type="button"
              onClick={quickAction.onClick}
              // Ring is flush (no offset) like the card's other over-photo controls — an offset
              // band would carve a stripe of the wrong color out of the image behind it.
              className="inline-flex items-center gap-1 rounded-full bg-white/90 py-2 pl-2.5 pr-3 text-xs font-bold text-brand-green-900 shadow-[0_1px_6px_rgba(30,51,25,0.15)] backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:bg-black/60 dark:text-white dark:hover:bg-black/80 dark:focus-visible:ring-pelorous-300">
              <Plus size={12} strokeWidth={2.5} />
              {quickAction.label}
            </button>
          </div>
        )}

        {!selectable && onFavoriteToggle && (
          // 44px hit area wrapping the 34px visual circle (mobile touch-target floor).
          <button
            type="button"
            onClick={onFavoriteToggle}
            aria-label={favoriteLabel}
            aria-pressed={favorite}
            className="group/fav absolute right-0.5 top-0.5 z-[2] grid h-11 w-11 place-items-center rounded-full focus-visible:outline-none">
            <span
              className={cn(
                'grid h-[34px] w-[34px] place-items-center rounded-full bg-white/90 shadow-[0_1px_6px_rgba(30,51,25,0.15)] backdrop-blur-sm',
                'transition-transform duration-150 group-hover/fav:scale-110 motion-reduce:transition-none motion-reduce:group-hover/fav:scale-100',
                'group-focus-visible/fav:ring-[3px] group-focus-visible/fav:ring-pelorous-500 dark:group-focus-visible/fav:ring-pelorous-300',
                'dark:bg-black/60',
                favorite ? 'text-red' : 'text-brand-green-900/45 dark:text-white/60'
              )}>
              <Heart size={17} fill={favorite ? 'currentColor' : 'none'} strokeWidth={2} />
            </span>
          </button>
        )}

        <div className="absolute bottom-2.5 left-2.5 z-[1]">
          {tag.variant === MealCardTagVariant.New ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber px-3 py-1 text-xs font-semibold text-white dark:text-brand-green-950">
              <Sparkle size={11} fill="currentColor" strokeWidth={0} />
              {tag.label}
            </span>
          ) : (
            <Chip variant="meta" tone="onImage">
              {tag.label}
            </Chip>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4 pt-3">
        <div className="flex items-start gap-1">
          <h3 className="min-w-0 flex-1 font-display text-base font-medium leading-tight tracking-[-0.01em]">
            {tapAction ? (
              // Stretched hit area: the button's ::after covers the card, so tapping
              // anywhere (outside the elevated heart/menu) opens — or, in select
              // mode, toggles — the meal.
              <button
                type="button"
                onClick={tapAction}
                {...(selectable ? { 'aria-pressed': selected, 'aria-label': selectLabel } : {})}
                // The ring is INSET. This ::after is inset-0 of the card, and the card is
                // overflow-hidden — an outward ring lands entirely outside the card's padding
                // box and gets clipped away, so focusing the card's primary action used to
                // paint nothing at all. Drawing inside the same box keeps it visible.
                className="text-left after:absolute after:inset-0 after:rounded-[22px] focus-visible:outline-none focus-visible:after:ring-[3px] focus-visible:after:ring-inset focus-visible:after:ring-pelorous-500 dark:focus-visible:after:ring-pelorous-300">
                {name}
              </button>
            ) : (
              name
            )}
          </h3>
          {!selectable && menu && <div className="relative z-[2] -mr-1.5 -mt-1 shrink-0">{menu}</div>}
        </div>

        <div className="mt-auto flex items-center gap-2 pt-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Clock size={13} className="shrink-0 text-faint" />
            {timeLabel}
          </span>
          {servingsLabel && (
            <>
              <span aria-hidden="true" className="h-[3px] w-[3px] shrink-0 rounded-full bg-faint" />
              <span className="whitespace-nowrap">{servingsLabel}</span>
            </>
          )}
          {roles && roles.length > 0 && (
            <span title={rolesLabel} className="ml-auto inline-flex shrink-0 items-center gap-1">
              {rolesLabel && <span className="sr-only">{rolesLabel}</span>}
              {roles.map((role) => (
                <PlateRoleChip key={role} shape="dot" role={role} className="h-[7px] w-[7px]" />
              ))}
            </span>
          )}
        </div>
      </div>
    </motion.article>
  )
}
